import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEditorBranchesV1BranchesServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/editor-branches',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/editor-branches',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/editor-branches',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/branches/v1/branches',
        destPath: '/v1/branches',
      },
      {
        srcPath: '/editor/branches/v1/branches',
        destPath: '/v1/branches',
      },
      {
        srcPath: '/editor/branches/v1/bulk/branches',
        destPath: '/v1/bulk/branches',
      },
      {
        srcPath: '/branches/v1/bulk/branches',
        destPath: '/v1/bulk/branches',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/editor-branches',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/editor-branches',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/editor-branches',
        destPath: '',
      },
    ],
    'vibe._base_domain_': [
      {
        srcPath: '/_api/editor-branches',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_editor-branches_branches';

/**
 * Creates a branch.
 *
 * If a source branch isn't provided, the branch will include a blank website.
 *
 * > **Important:** Align the `sourceType` field with its corresponding source properties:
 * > - When `sourceType` is `SOURCE_BRANCH`, provide `sourceBranchProperties`.
 * > - When `sourceType` is `SOURCE_TEMPLATE`, provide `sourceTemplateProperties`.
 * >
 * > Using `UNKNOWN_SOURCE_TYPE` or mismatched properties results in an `ILLEGAL_SOURCE_TYPE` error.
 */
export function createBranch(payload: object): RequestOptionsFactory<any> {
  function __createBranch({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'branch.createdDate' }, { path: 'branch.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'POST' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.CreateBranch',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/branches',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'branch.createdDate' },
              { path: 'branch.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createBranch;
}

/** Retrieves a branch. */
export function getBranch(payload: object): RequestOptionsFactory<any> {
  function __getBranch({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'GET' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.GetBranch',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/branches/{branchId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'branch.createdDate' },
              { path: 'branch.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getBranch;
}

/**
 * Updates a branch.
 *
 * Each time the branch is updated, `revision` increments by 1. The current `revision` must be passed when updating the branch. This ensures you're working with the latest branch and prevents unintended overwrites.
 *
 * Note that `ORIGINAL_BRANCH` types can't be updated.
 */
export function updateBranch(payload: object): RequestOptionsFactory<any> {
  function __updateBranch({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'branch.createdDate' }, { path: 'branch.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'PATCH' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.UpdateBranch',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/branches/{branch.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'branch.createdDate' },
              { path: 'branch.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBranch;
}

/** Permanently deletes a branch. */
export function deleteBranch(payload: object): RequestOptionsFactory<any> {
  function __deleteBranch({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'DELETE' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.DeleteBranch',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/branches/{branchId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteBranch;
}

/**
 * Retrieves a list of branches, given the provided paging, filtering, and sorting.
 *
 *
 * Query Branches runs with these defaults, which you can override:
 *
 * - `updatedDate` is sorted in `DESC` order
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * To learn how to query branches, see [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language).
 */
export function queryBranches(payload: object): RequestOptionsFactory<any> {
  function __queryBranches({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'GET' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.QueryBranches',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/branches/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'branches.createdDate' },
              { path: 'branches.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixEditorBranchesV1BranchesServiceUrl({
            protoPath: '/v1/branches/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryBranches;
}

/**
 * Sets the specified branch as the default.
 *
 * The default branch is used for API operations when no branch ID is specified.
 */
export function setDefaultBranch(payload: object): RequestOptionsFactory<any> {
  function __setDefaultBranch({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'POST' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.SetDefaultBranch',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/branches/{branchId}/set-default',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'branch.createdDate' },
              { path: 'branch.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setDefaultBranch;
}

/** Retrieves the default branch. */
export function getDefaultBranch(payload: object): RequestOptionsFactory<any> {
  function __getDefaultBranch({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'GET' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.GetDefaultBranch',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/branches/default',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'branch.createdDate' },
              { path: 'branch.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDefaultBranch;
}

/**
 * Updates tags on multiple branches.
 *
 * Specify which tags to assign and which tags to unassign. A tag that appears in both the list to assign and the list to unassign will be assigned.
 *
 * To update a single branch's tags, call [Update Branch](https://dev.wix.com/docs/api-reference/business-management/branches/update-branch).
 */
export function bulkUpdateBranchTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateBranchTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'POST' as any,
      methodFqn: 'wix.editor.branches.v1.BranchesService.BulkUpdateBranchTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/bulk/branches/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateBranchTags;
}

/**
 * Asynchronously updates tags on multiple branches that match the provided filter criteria.
 *
 * Specify which tags to assign and which tags to unassign. A tag that appears both the list to assign and the list to unassign tags, will be assigned.
 *
 * To check the status of the bulk update, pass the returned `jobId` to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job).
 *
 * To update a single branch's tags, call [Update Branch](https://dev.wix.com/docs/api-reference/business-management/branches/update-branch).
 */
export function bulkUpdateBranchTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateBranchTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.editor.branches.v1.branch',
      method: 'POST' as any,
      methodFqn:
        'wix.editor.branches.v1.BranchesService.BulkUpdateBranchTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEditorBranchesV1BranchesServiceUrl({
        protoPath: '/v1/bulk/branches/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateBranchTagsByFilter;
}
